import { createRootRoute, createRoute, createRouter, Navigate } from '@tanstack/react-router';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { AppShell } from '@/app/AppShell';
import { More } from '@/routes/More';
import { MyTowns } from '@/features/towns/MyTowns';
import { SignalFeed } from '@/features/feed/SignalFeed';
import { MeetingRoute } from '@/features/meeting/MeetingRoute';
import { Search } from '@/features/search/Search';
import { Signals } from '@/features/signals/Signals';
import { RecordsList } from '@/features/records/RecordsList';
import { RecordDetail } from '@/features/records/RecordDetail';
import { PlanChangeRoute } from '@/features/billing/PlanChangeRoute';
import { TopicTrends } from '@/features/topics/TopicTrends';
import { Account } from '@/features/billing/Account';
import { Pricing } from '@/features/pricing/Pricing';
import { AskAware } from '@/features/ask/AskAware';
import { InterstitialRoute, PlanChoiceRoute } from '@/features/onboarding/OnboardingRoutes';
import { Callback } from '@/features/auth/Callback';
import { CheckoutRoute } from '@/features/billing/CheckoutRoute';
import { SignInRoute } from '@/features/auth/SignInRoute';
import { homeRoute } from '@/lib/tier';

const rootRoute = createRootRoute({ component: AppShell });

/**
 * The origin. Two different jobs depending on who is asking.
 *
 * Signed in, it is not a surface — My Feed is the landing route at every tier, so it forwards
 * there. Deep links survive the Kinde round trip separately, through the OAuth `state`
 * parameter; this only handles a bare visit to the origin.
 *
 * SIGNED OUT, IT MUST RENDER A REAL PAGE ON THIS DOMAIN. It previously forwarded to `/feed`,
 * which the shell bounced to `/sign-in`, which redirected straight to the identity provider —
 * so anyone visiting the origin left the domain before seeing anything. That breaks more than
 * first impressions: the payment provider will not approve a checkout domain it cannot load a
 * page on, and an automated reviewer following that chain sees an off-site redirect, not a
 * product. The same is true of a link preview, a search crawler, or anyone deciding whether
 * this address is legitimate before signing in.
 */
function HomeRoute() {
  const { isAuthenticated, isLoading } = useKindeAuth();
  if (isLoading) return null;
  if (isAuthenticated) return <Navigate to={homeRoute()} replace />;
  return <InterstitialRoute />;
}

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: HomeRoute,
});

/**
 * Every surface is registered from day one so navigation, tier gating and deep links can be
 * exercised before the screens exist. Routes are declared explicitly rather than generated in
 * a loop — TanStack Router derives its path types from this tree, and a mapped array erases
 * them. Replace each `Soon` with the real component as the surface lands; the paths and the
 * build order below are the plan of record.
 */
const townsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/feed',
  component: MyTowns,
});

const feedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signals',
  component: SignalFeed,
});

const meetingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/meeting/$meetingId',
  component: MeetingRoute,
});

const searchRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/search',
  component: Search,
});

const signalsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signals/manage',
  component: Signals,
});

const legislativeHistoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/legislative-history',
  component: RecordsList,
});

const recordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/legislative-history/$recordId',
  component: RecordDetail,
});

const topicTrendsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/topic-trends',
  component: TopicTrends,
});

const askRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/ask',
  component: AskAware,
});

const accountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/account',
  component: Account,
});

/**
 * Pre-auth onboarding. These sit under the shell route for now; in production they render on
 * their own shell with no nav, since the user is not authenticated yet.
 */
const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signup',
  component: InterstitialRoute,
});

/** Where the provider sends the customer to pay. Bare — the checkout overlay owns the page. */
const checkoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/checkout',
  component: CheckoutRoute,
});

const planPricingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/plan-pricing',
  component: PlanChoiceRoute,
});

/** Phone-only overflow screen; redundant above the nav breakpoint. */
const moreRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/more',
  component: More,
});

/** Between plan selection and the checkout overlay. */
const planChangeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/account/change-plan',
  component: PlanChangeRoute,
});

const pricingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pricing',
  component: Pricing,
});

/**
 * Our own sign-in screen. The shared Kinde application has "use your own screens" enabled, so
 * this is the front door rather than a redirect to a hosted page.
 */
const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in',
  component: SignInRoute,
});

/**
 * The OAuth landing route. Registered in Kinde as an allowed callback URL for every origin
 * this app is served from. Deliberately separate from "/" so the code exchange cannot race the
 * home redirect — see `features/auth/Callback.tsx`.
 */
const callbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/callback',
  component: Callback,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  townsRoute,
  feedRoute,
  meetingRoute,
  searchRoute,
  signalsRoute,
  legislativeHistoryRoute,
  recordRoute,
  topicTrendsRoute,
  askRoute,
  accountRoute,
  planChangeRoute,
  signupRoute,
  planPricingRoute,
  checkoutRoute,
  moreRoute,
  pricingRoute,
  signInRoute,
  callbackRoute,
]);

export const router = createRouter({
  routeTree,
  // Prefetch on intent (hover, touchstart) — the cheapest win available on a static app, and
  // the reason route transitions can hold the sub-100ms budget on a phone.
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 30_000,
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
